import type { Collection } from '@tanstack/db';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';

export type CollectionMutationEnvelope = { mutations: Record<string, unknown>[] };

const isAcceptMutationsFn = (value: unknown): value is (mutation: CollectionMutationEnvelope) => void =>
  typeof value === 'function';

export function tryAcceptCollectionMutations<T extends object>(
  collection: Collection<T, string>,
  tx: unknown,
  options: { suppressDuplicate?: boolean } = {}
): void {
  if (!isRecord(tx)) {
    return;
  }

  const mutationsValue = getRecordValue(tx, 'mutations');
  if (!(Array.isArray(mutationsValue) && mutationsValue.every((item) => isRecord(item)))) {
    return;
  }

  // TanStack DB's `Collection` type doesn't currently expose `utils` as an indexable property.
  const utilsValue = isRecord(collection) ? getRecordValue(collection, 'utils') : null;
  const accepterCandidate = isRecord(utilsValue) ? getRecordValue(utilsValue, 'acceptMutations') : null;
  if (!isAcceptMutationsFn(accepterCandidate)) {
    return;
  }

  try {
    accepterCandidate({ mutations: mutationsValue });
  } catch (error) {
    if (
      options.suppressDuplicate &&
      error instanceof Error &&
      error.message.includes('already exists in the collection')
    ) {
      return;
    }
    throw error;
  }
}
